import { useCallback } from "react";
import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { readEnvironmentApi } from "../../environmentApi";
import { readLocalApi } from "../../localApi";
import { type Thread } from "../../types";
import { newCommandId } from "~/lib/utils";

export function useCheckpointRevert(input: {
  readonly environmentId: EnvironmentId;
  readonly activeThread: Thread | undefined;
  readonly phase: string;
  readonly isSendBusy: boolean;
  readonly isConnecting: boolean;
  readonly isRevertingCheckpoint: boolean;
  readonly setIsRevertingCheckpoint: (isReverting: boolean) => void;
  readonly setThreadError: (targetThreadId: ThreadId | null, error: string | null) => void;
}): {
  readonly onRevertToTurnCount: (turnCount: number) => Promise<void>;
} {
  const onRevertToTurnCount = useCallback(
    async (turnCount: number) => {
      const api = readEnvironmentApi(input.environmentId);
      const localApi = readLocalApi();
      if (!api || !localApi || !input.activeThread || input.isRevertingCheckpoint) return;

      if (input.phase === "running" || input.isSendBusy || input.isConnecting) {
        input.setThreadError(
          input.activeThread.id,
          "Interrupt the current turn before reverting checkpoints.",
        );
        return;
      }
      const confirmed = await localApi.dialogs.confirm(
        [
          `Revert this thread to checkpoint ${turnCount}?`,
          "This will discard newer messages and turn diffs in this thread.",
          "This action cannot be undone.",
        ].join("\n"),
      );
      if (!confirmed) {
        return;
      }

      input.setIsRevertingCheckpoint(true);
      input.setThreadError(input.activeThread.id, null);
      try {
        await api.orchestration.dispatchCommand({
          type: "thread.checkpoint.revert",
          commandId: newCommandId(),
          threadId: input.activeThread.id,
          turnCount,
          createdAt: new Date().toISOString(),
        });
      } catch (err) {
        input.setThreadError(
          input.activeThread.id,
          err instanceof Error ? err.message : "Failed to revert thread state.",
        );
      }
      input.setIsRevertingCheckpoint(false);
    },
    [input],
  );

  return { onRevertToTurnCount };
}
